/**
 * The guide, at /help.
 *
 * WHY IT LIVES IN THE APP rather than as a document he has to find. A manual kept somewhere else
 * is a manual that goes stale the first time the thing it describes changes, and nobody notices,
 * because nothing connects the two. Here it ships with the build it documents.
 *
 * Written to be PRINTED as well as read: the print rules at the bottom of help.css drop the deck's
 * dark ground, let sections break cleanly, and expand links so a paper copy still says where things
 * are. He asked for a PDF, and a page that prints properly is a better PDF than a generated one.
 *
 * The images live under /guide, NOT /help. A public/help directory would make `/help` a directory
 * on the static server and shadow this very route, which is exactly what happened the first time.
 *
 * The screenshots are real renders of this app's own compiled CSS, captured from the layout
 * harnesses rather than from his live vault, so the guide carries no client name, no real person
 * and no confidential note. That is deliberate and worth keeping: a guide is the most likely
 * document to be shown to somebody else.
 */
import { useEffect, useState } from 'react';
import './help.css';

interface Section {
  id: string;
  title: string;
  body: JSX.Element;
}

/** A screenshot with its caption. Captions say what to LOOK at, never just what the picture is. */
function Shot({ src, alt, caption }: { src: string; alt: string; caption: string }): JSX.Element {
  return (
    <figure className="help-shot">
      <img src={src} alt={alt} loading="lazy" />
      <figcaption>{caption}</figcaption>
    </figure>
  );
}

const SECTIONS: Section[] = [
  {
    id: 'what',
    title: 'What MO OS is',
    body: (
      <>
        <p>
          MO OS reads your working correspondence and distils it into typed, linked notes you own as
          plain Markdown files. It is not a chatbot with your documents attached. It keeps a model of
          your working life: who you deal with, what you have promised, what is waiting on you, and
          what has gone quiet.
        </p>
        <p>
          Everything it knows lives in a vault of ordinary <code>.md</code> files. If MO OS
          disappeared tomorrow, the vault would open in Obsidian and lose nothing. That is the point:
          the system is replaceable and the record is not.
        </p>
        <h3>The four layers</h3>
        <table className="help-table">
          <thead>
            <tr><th>Layer</th><th>What it does</th></tr>
          </thead>
          <tbody>
            <tr><td><strong>MO OS</strong></td><td>The brain. Reads, distils, remembers, answers.</td></tr>
            <tr><td><strong>Hermes</strong></td><td>The PA. Morning brief, digests, scheduled jobs, alerts.</td></tr>
            <tr><td><strong>Hibiki</strong></td><td>The social specialist. Drafts and publishes LinkedIn and Instagram.</td></tr>
            <tr><td><strong>Kantoku</strong></td><td>Your personal brand director. Daily video topics, clips cut from what you record, captions in your voice, posting to @moforslive once you approve.</td></tr>
            <tr><td><strong>The deck</strong></td><td>What you read. Attention, commitments, pipeline, graph.</td></tr>
          </tbody>
        </table>
        <p className="help-rule">
          One rule shapes almost everything below: <strong>MO OS proposes, you decide.</strong> It
          never moves a CRM stage, never sends mail as you, and never publishes without your yes.
        </p>
      </>
    ),
  },
  {
    id: 'deck',
    title: 'The deck',
    body: (
      <>
        <p>
          The landing view, at <code>/</code>. It answers one question: what deserves your attention
          right now.
        </p>
        <Shot
          src="/guide/deck.png"
          alt="The MO OS command deck"
          caption="The deck. The rail on the left is navigation; the hero states what needs judgement; the right column is your next meeting and what is coming."
        />
        <h3>What each part is for</h3>
        <ul>
          <li><strong>Attention</strong> is the queue of things only you can settle: identity questions, low-confidence readings, commitments coming due.</li>
          <li><strong>Priority signal</strong> is what is flagged now, ordered by what it would cost to miss.</li>
          <li><strong>Next meeting</strong> shows the brief, the commitments linked to that person, and open questions. The Prepare button assembles the lot.</li>
          <li><strong>Since you last checked</strong> is the strip of what changed while you were away, and how long each has waited.</li>
          <li><strong>Talking to MO</strong> is the console. See below.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'chat',
    title: 'Asking it things',
    body: (
      <>
        <p>
          Two personas, and the difference matters. <strong>MO</strong> knows the vault and answers
          from it. <strong>Zou</strong> composes new text: drafts, replies, materials. Nothing Zou
          writes is a record of anything until you make it one.
        </p>
        <Shot
          src="/guide/chat.png"
          alt="The MO OS chat console"
          caption="The console. The two lanes are a choice between reading and writing, not a style setting."
        />
        <h3>Questions that work well</h3>
        <ul>
          <li>&quot;What do I owe Ana, and since when?&quot;</li>
          <li>&quot;Which accounts have gone quiet for more than a month?&quot;</li>
          <li>&quot;What did we agree in the last GSK meeting?&quot;</li>
          <li>&quot;Prepare me for my next meeting.&quot;</li>
        </ul>
        <p>
          It answers from notes it can cite. If it has no evidence, it says so rather than producing
          something plausible. That is the single most important behaviour in the system.
        </p>
      </>
    ),
  },
  {
    id: 'notes',
    title: 'Notes, search and the graph',
    body: (
      <>
        <p>
          The left rail carries files, search and tags. Notes are typed: person, organisation, topic,
          thread, meeting, commitment, decision, objective.
        </p>
        <Shot
          src="/guide/dossier.png"
          alt="A person dossier"
          caption="A person dossier: their role, what you owe them, what they owe you, and the engagements they appear in."
        />
        <Shot
          src="/guide/search.png"
          alt="Search results"
          caption="Search. The row you are pointing at carries a coloured edge, the same signal the file rail uses."
        />
        <h3>The graph</h3>
        <p>
          Press <kbd>Cmd</kbd>+<kbd>G</kbd> or the graph icon. It opens in 3D, which you can grab and
          rotate. The <strong>2D</strong> button in the top-right corner switches to the flat chart,
          which also carries the timelapse and the force sliders. Your choice is remembered.
        </p>
        <p>
          Both views colour by what a note <em>is</em>: people, organisations, topics, commitments,
          meetings, correspondence, decisions, objectives. The legend lists only the kinds actually on
          screen. The 3D view draws the most connected 1,200 nodes by default and says so, because
          drawing all seven thousand freezes the tab.
        </p>
      </>
    ),
  },
  {
    id: 'health',
    title: 'Knowing it still works',
    body: (
      <>
        <p>
          The strip above the deck says whether MO OS itself is working. Green reads{' '}
          <strong>All lanes holding</strong>. It is always present, because an absent warning and a
          broken watchdog look identical.
        </p>
        <Shot
          src="/guide/strip.png"
          alt="The assurance strip showing a fault"
          caption="A fault names the lane. It does not just say something is wrong, which would leave you where you started."
        />
        <p>
          Each ingestion lane is judged on what it <em>changed</em>, not on whether it ran. That
          distinction exists because every serious defect found in this system ran to completion,
          reported success, and did nothing.
        </p>
        <h3>Click the strip</h3>
        <Shot
          src="/guide/forge.png"
          alt="The forge, showing a fault brief"
          caption="The forge. It states the fault, offers a likely cause as a hypothesis to check, and builds a brief you can paste into a fresh Claude session."
        />
        <p>
          The brief is the useful part. It carries the file to start in, the observation in numbers,
          and the standing rules a well-meant fix would otherwise break. Secrets are stripped from it
          by construction, because its whole purpose is to be pasted somewhere else.
        </p>
        <h3>What the states mean</h3>
        <table className="help-table">
          <thead><tr><th>State</th><th>Meaning</th></tr></thead>
          <tbody>
            <tr><td>Healthy</td><td>It ran and changed something, or honestly reported nothing to do.</td></tr>
            <tr><td>Degraded</td><td>It ran and changed nothing it should have. Worth a look, not an emergency.</td></tr>
            <tr><td>Quarantined</td><td>It errored, went silent past its window, or never reported. Conclusions drawn from it are withheld from the deck.</td></tr>
          </tbody>
        </table>
        <p className="help-rule">
          On a quarantined lane the deck keeps the <strong>facts</strong> and drops the{' '}
          <strong>conclusions</strong>. &quot;Last contact 24 August&quot; stays. &quot;You owe them a
          reply&quot; goes, because a reading of stale evidence is how you end up chasing a dead deal.
        </p>
      </>
    ),
  },
  {
    id: 'crm',
    title: 'The CRM',
    body: (
      <>
        <p>
          MO OS keeps Twenty honest without taking it over. Every fifteen minutes it folds what your
          mail and calendar saw into each followed account, and writes the follow-up date and reason.
        </p>
        <h3>What it will and will not do</h3>
        <ul>
          <li><strong>It writes</strong> the follow-up date, the reason, and a relationship note when something material changes.</li>
          <li><strong>It creates</strong> one opportunity for a followed account that has none, on that account&apos;s pinned company, at the entry stage.</li>
          <li><strong>It proposes</strong> a stage change, with its reason, as a question on the row.</li>
          <li><strong>It never moves a stage.</strong> That is yours, and it always has been.</li>
          <li><strong>It never creates</strong> a person or a company. Your contacts arrive by M365 sync and are yours to curate.</li>
        </ul>
        <p>
          Direction is the point of the pipeline panel. &quot;Last contact 14 days ago&quot; is
          identical for an account ignoring you and one you have not replied to, and the two call for
          opposite actions. So every row names who owes the next move, read from the last message,
          never guessed from elapsed time.
        </p>
      </>
    ),
  },
  {
    id: 'social',
    title: 'Publishing',
    body: (
      <>
        <p>
          Hibiki drafts LinkedIn and Instagram posts and sends them to your Telegram. Nothing reaches
          the public without your reply.
        </p>
        <h3>The loop</h3>
        <ol>
          <li>A scheduled job sends you one post and marks it <code>awaiting_approval</code>.</li>
          <li>You reply <code>approve</code>, <code>skip</code>, or <code>edit: …</code>.</li>
          <li>Your decision is written to a file, not held in a conversation.</li>
          <li>The publisher posts that one entry on its next run.</li>
        </ol>
        <p className="help-rule">
          Step three is the part that used to fail. Scheduled runs are stateless: the job that asked
          has ended by the time you reply, so your reply arrives in a session with no memory of it.
          The file is what carries the decision between the two.
        </p>
        <p>
          One post publishes per run, never a batch, and anything already published can never go out
          twice.
        </p>
      </>
    ),
  },
  {
    id: 'scenarios',
    title: 'Scenarios',
    body: (
      <>
        <h3>You forward a thread to the assistant address</h3>
        <p>
          Within a few minutes it is distilled into a thread note, and the people, organisations and
          topics in it are revised rather than re-authored, so nothing that was already known is lost.
          Commitments found in it appear in your attention queue with a link back to the
          correspondence that created them.
        </p>

        <h3>A meeting happens</h3>
        <p>
          Read AI sends the transcript; MO OS reads the <em>note</em>, never the transcript, which is
          the whole confidentiality argument. It names a pattern only when the meeting shows one and
          quotes the evidence, and it proposes follow-ups only where a commitment does not already say
          the same thing.
        </p>

        <h3>A deal dies</h3>
        <p>
          When the other side says it went elsewhere, that now registers as a loss signal, and MO OS
          proposes moving the opportunity to dormant with its reason. It still does not move it. You
          do, and the proposal is there so you are not deciding from memory.
        </p>

        <h3>A lane breaks</h3>
        <p>
          The strip turns amber or red and names the lane. Hermes sends you one message on Telegram,
          once, not once per check. The morning brief carries a line either way. You open the forge,
          copy the brief, and paste it into a fresh session that then has everything it needs.
        </p>

        <h3>Two notes turn out to be the same person</h3>
        <p>
          It raises the question rather than merging. Identity is yours: a wrong merge is not
          reversible in your head, whatever the database does. Once you decide, the merge is recorded
          with your name on it and a tombstone keeps the old path working.
        </p>
      </>
    ),
  },
  {
    id: 'rules',
    title: 'The rules it will not break',
    body: (
      <>
        <ul className="help-rules">
          <li><strong>A guessed citation is worse than an absent one.</strong> It will leave a gap rather than fill it with something plausible.</li>
          <li><strong>You decide identity.</strong> Merges are proposed, recorded, and reversible.</li>
          <li><strong>You move stages.</strong> It proposes and never applies.</li>
          <li><strong>It never sends mail as you.</strong> That permission was deliberately not granted.</li>
          <li><strong>It never deletes a note.</strong> Tombstones, except for things that were never people.</li>
          <li><strong>Nothing external carries the internal skin.</strong> Client decks, drafted client mail, public links and published posts follow the Vantage design.</li>
          <li><strong>A closed commitment is not evidence of work.</strong> 1,655 were closed in one bulk reset; each says so on its own note.</li>
        </ul>
      </>
    ),
  },
];

export function Help(): JSX.Element {
  const [active, setActive] = useState(SECTIONS[0]!.id);

  /* Highlight the section you are actually reading, so a long page still says where you are. */
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        const seen = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (seen[0]) setActive(seen[0].target.id);
      },
      { rootMargin: '-80px 0px -70% 0px' },
    );
    for (const s of SECTIONS) {
      const el = document.getElementById(s.id);
      if (el) obs.observe(el);
    }
    return () => obs.disconnect();
  }, []);

  return (
    <main className="help">
      <header className="help-head">
        <h1>MO OS</h1>
        <p className="help-sub">How it works, what it will do, and what it refuses to do.</p>
        <button type="button" className="help-print" onClick={() => window.print()}>
          Print or save as PDF
        </button>
      </header>

      <nav className="help-toc" aria-label="Contents">
        <ol>
          {SECTIONS.map((s) => (
            <li key={s.id} data-active={s.id === active ? 'true' : undefined}>
              <a href={`#${s.id}`}>{s.title}</a>
            </li>
          ))}
        </ol>
      </nav>

      <div className="help-body">
        {SECTIONS.map((s) => (
          <section key={s.id} id={s.id} className="help-section">
            <h2>{s.title}</h2>
            {s.body}
          </section>
        ))}
        <footer className="help-foot">
          <p>
            The screenshots are renders of this build&apos;s own interface with sample data, not of
            your vault, so this guide carries no client name and no confidential note. It ships with
            the build it describes.
          </p>
        </footer>
      </div>
    </main>
  );
}
